import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { locationShape } from 'react-router';
import InfiniteScroll from 'react-infinite-scroller';
import lodash from 'lodash';

import styles from './Files.scss';
import iconStyles from 'styles/icons/folder-icon.scss';
import { loadDocuments, loadDescriptorSystemNames, setFileState, removeFiles } from 'binder/actions/files';
import { loadTasksByBinderId } from 'pages/binder/actions/binderActions';
import { loadMimeTypes } from 'common/actions/fileUploadActions';
import { setSortingOptions } from 'common/actions/fileSortingOptions';
import { setActionButton, clearActionButton, buildActionButtonProperty } from 'common/actions/actionButtonActions';
import { buildSidebarActions } from './buildSidebarActions';
import { binderInstitutionIdSelector } from 'common/selectors';
import { UserCanUploadFilesByDragAndDrop } from 'common/permissions/components/unfiled';
import { getExpirationDateFromDocument } from 'common/util/getExpirationDateFromDocument';
import { isLoggedInUserInternalUserForCurrentBinderSelector, manageTasksSelector } from 'common/permissions/selectors';
import { unfiledDocumentsPredicate } from 'common/permissions/predicates';
import File from 'common/components/File/File';
import FilePicker from 'components/common/File/FilePicker';
import DragAndDrop from 'components/common/File/DragAndDrop';
import SearchBar from 'components/common/SearchBar/SearchBar';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import {SortingOptions, SortingOption} from 'components/common/SearchBar/SortingOptions';
import {SearchFeedback} from 'components/common/SearchBar/SearchFeedback';
import ListHelperText from 'components/common/Helper/ListHelper';
import ListControls  from 'components/common/Layout/ListControls';
import UnfiledDocuments from 'pages/files/containers/Unfiled';
import { pathSubDirectorySelector } from 'binder/selectors/files';

const DEFAULT_LIMIT = 50;
const DEFAULT_OFFSET = 0;

class Files extends Component {
    constructor() {
        super();
        this.state = {
            DragAndDropComponent: this.getDragAndDropComponent()
        };
        this.debouncedScrollToBottom = lodash.debounce(this.onScrollToBottom.bind(this), 100);
        this.getScrollParent = this.getScrollParent.bind(this);
    }

    getScrollParent() {
        return document.querySelector('[data-cy="AppContainer-return"]');
    }

    updateLocation(location) {
        const { router } = this.context;
        router.replace(location);
    }

    changeQueryKey(queryKey, value) {
        const { location } = this.context;
        const newLocation = lodash.merge({}, location, { query: { [queryKey]: value } });
        this.updateLocation(newLocation);
    }

    isBinderAvailable(binder) {
        return !lodash.isNil(binder) && !lodash.isNil(binder.id);
    }

    setDefaultSortOptions(binder, folderId, query) {
        const folders = lodash.get(binder, 'layout.folders', []);
        let folder;
        if (folderId) {
            folder = lodash.find(folders, f => f.id === folderId);
        } else {
            folder = lodash.find(folders, f => f.parentId === null);
        }

        const defaultSortOptions = lodash.get(folder, 'options.sort', []);

        if (lodash.isEmpty(query) && !lodash.isEmpty(defaultSortOptions)) {
            lodash.assign(query, { o: defaultSortOptions[0].direction === 'ascending' ? '1' : '-1' });
        }
        return query;
    }

    initializePage() {
        const { binder, folderId, loadDescriptorSystemNames, setSortingOptions } = this.props;

        const pathName = pathSubDirectorySelector(this.props.location.pathname);
        const redirectLink = pathName === 'study' && this.props.study ?
            `/study/${this.props.study.id}/documents?folder=unfiled&offset=0&upload=true`
            :
            `/binder/${binder.id}/files?folder=unfiled&offset=0&upload=true`;

        if (this.isBinderAvailable(binder)) {
            setSortingOptions(folderId);
            this.actionButtonProperty = buildActionButtonProperty('Add Documents', redirectLink, 'uploadFiles');
            let query = this.props.location.query;
            query = this.setDefaultSortOptions(binder, folderId, query);
            this.loadDocuments(binder, folderId, query);
            this.loadTasks(binder);
            loadDescriptorSystemNames();
            this._buildSidebarActions(binder);
            this.props.setActionButton(this.actionButtonProperty);
        }
    }

    createSearchOptions(folderId, query) {
        const parsedOffset = parseInt(query.offset, 10);
        let offset = lodash.isFinite(parsedOffset) ? parsedOffset : this.props.offset;
        const isUnfiledDocuments = query.folder === 'unfiled';
        return {
            folderId,
            unfiledDocuments: isUnfiledDocuments,
            sort: this.getSearchOptionsSort(isUnfiledDocuments, query),
            limit: parseInt(query.limit) || this.props.limit,
            searchString: query.q,
            offset: offset
        };
    }

    getSearchOptionsSort(isUnfiledDocuments, query) {
        const defaultSort = isUnfiledDocuments ? 'uploadDate' : 'default';
        return [{ field: query.s || defaultSort, direction: query.o === '1' ? 'ASCENDING' : 'DESCENDING' }];
    }

    loadDocuments(binder, folderId, query = {}, replace = false) {
        const { loadDocuments } = this.props;
        binder = binder || this.props.binder;
        query = query || this.props.location.query;
        query = this.setDefaultSortOptions(binder, folderId, query);

        const binderId = lodash.get(binder, 'id');
        const searchOptions = this.createSearchOptions(folderId, query);
        loadDocuments(binderId, searchOptions, replace);
    }

    loadTasks(binder) {
        const { loadTasksByBinderId, canManageTasks } = this.props;
        if (canManageTasks) {
            loadTasksByBinderId(binder.id);
        }
    }

    clearQuery() {
        const { router, location } = this.props;
        const newLocation = lodash.cloneDeep(location);
        newLocation.query = {
            ['q']: ''
        };
        router.replace(newLocation);
    }

    componentDidMount() {
        this.props.loadMimeTypes();
        this.props.setInitialOffset(this.props.offset);
        this.initializePage();
    }

    componentDidUpdate(prevProps) {
        if (!this.isBinderAvailable(prevProps.binder) && this.isBinderAvailable(this.props.binder)) {
            this.initializePage();
        }
    }

    componentWillUnmount() {
        const { setSidebarLinks } = this.props;
        setSidebarLinks([]);
        this.props.clearActionButton();
        this.props.removeFiles();
    }

    componentWillReceiveProps(nextProps) {
        const currentBinder = this.props.binder;
        const nextBinder = nextProps.binder;
        const currentLocation = this.props.location;
        const nextLocation = nextProps.location;
        const enteringUnfiledDocuments = !this.props.isUnfiledDocuments && nextProps.isUnfiledDocuments;
        const errorLoadingUnfiledDocuments = this.props.isUnfiledDocuments && this.props.unfiledDocumentsLoadingError;
        const leavingUnfiledDocuments = this.props.isUnfiledDocuments && !nextProps.isUnfiledDocuments;
        const newConvertedDocumentIsfetchable = this.props.hasNewDocumentConversionResult && this.props.isUnfiledDocuments;
        const isAddingDocumentFromBinderList = nextLocation.query.upload && nextLocation.query.binderAction;
        const currentFolder = lodash.get(currentLocation, 'query.folder');
        const nextFolder = lodash.get(nextLocation, 'query.folder');
        const isTransitioningToAnotherFolder = currentFolder !== nextFolder;
        if (!this.isBinderAvailable(currentBinder) && this.isBinderAvailable(nextBinder)) {
            this._buildSidebarActions(nextBinder);
            this.loadDocuments(nextBinder, nextProps.folderId, lodash.omit(nextLocation.query, ['offset']));
            return;
        }
        if (Files.didUnfiledDocumentCountUpdate(currentBinder, nextBinder)) {
            this._buildSidebarActions(nextBinder);
        }
        if(newConvertedDocumentIsfetchable) {
            const nextLocationQueryWithoutQueryString = lodash.omit(nextLocation.query, ['q']);
            return this.loadDocuments(nextBinder, nextProps.folderId, nextLocationQueryWithoutQueryString, 'replace');
        }
        if(leavingUnfiledDocuments) {
            const nextLocationQueryWithoutUnfiledBits = lodash.omit(nextLocation.query, ['offset', 's', 'o', 'q']);
            this.clearQuery();
            return this.loadDocuments(nextBinder, nextProps.folderId, nextLocationQueryWithoutUnfiledBits);
        }
        if(enteringUnfiledDocuments || errorLoadingUnfiledDocuments) {
            return this.loadUnfiledDocuments(nextBinder);
        }
        if (isAddingDocumentFromBinderList || nextLocation.query.upload) {
            return this.refs.filePicker.open();
        }
        if(isTransitioningToAnotherFolder) {
            this.transitionToAnotherFolder(nextBinder, nextFolder, nextLocation);
        }
        if (!lodash.isEqual(currentLocation.query, nextLocation.query) && this.isBinderAvailable(nextBinder)) {
            const method = lodash.isEqual(
                lodash.omit(currentLocation.query, 'offset'),
                lodash.omit(nextLocation.query, 'offset')) ? 'append' : 'replace';
            this._buildSidebarActions(nextBinder, nextLocation);
            this.loadDocuments(nextBinder, nextProps.folderId, nextLocation.query, method);
        }

    }

    transitionToAnotherFolder(binder, folderId, location) {
        const folder = lodash.get(binder, ['folders', folderId]);
        const folderDefaultSortOptions = lodash.get(folder, ['options', 'sort']);
        const queryOptionsToOmit = lodash.isEmpty(folderDefaultSortOptions) ? ['s', 'o'] : ['s'];
        location.query = lodash.omit(location.query, queryOptionsToOmit);

        if(this.refs.FilesSearchBar){
            this.refs.FilesSearchBar.clearQuery();
        }

        this.props.router.replace(location);
    }

    loadUnfiledDocuments(binder) {
        if(this.isBinderAvailable(binder)) {
            const defaultUnfiledDocumentsQuery = {folder:'unfiled', s:'uploadDate', o: '-1'};
            return this.loadDocuments(binder, 'unfiled', defaultUnfiledDocumentsQuery, 'replace');
        }
    }

    getDragAndDropComponent() {
        return UserCanUploadFilesByDragAndDrop(DragAndDrop);
    }

    getUnfiledDocuments() {
        const { binder, institutionId, allowedMimeTypes, fileExtensionLookup, location, router } = this.props;
        const { store } = this.context;
        const { DragAndDropComponent } = this.state;
        return (
            <DragAndDropComponent
                binderId={lodash.get(binder, 'id')}
                institutionId={institutionId}
                allowedMimeTypes={allowedMimeTypes}
                fileExtensionLookup={fileExtensionLookup}
                store={store}>
                <UnfiledDocuments
                    router={router}
                    location={location} />
                <FilePicker
                    ref="filePicker"
                    location={location}
                    onClose={() => this.changeQueryKey('upload', null)}
                    binderId={lodash.get(binder, 'id')}
                    institutionId = {institutionId}
                    allowedMimeTypes={allowedMimeTypes}
                    fileExtensionLookup={fileExtensionLookup}
                    store={store}/>
            </DragAndDropComponent>
        );
    }

    static didUnfiledDocumentCountUpdate(currentBinder, nextBinder) {
        return !lodash.eq(lodash.get(currentBinder, 'countUnfiledDocuments'), lodash.get(nextBinder, 'countUnfiledDocuments'));
    }

    onScrollToBottom() {
        const { folderId, location: { query }, documents, documentCount, loadingDocuments } = this.props;
        if (loadingDocuments) {
            return;
        }
        const searchOptions = this.createSearchOptions(folderId, query);
        if (documents.length < documentCount) {
            searchOptions.offset += searchOptions.limit;
            this.changeQueryKey('offset', searchOptions.offset);
        }
    }

    performSearch(query = ''){
        const { location, router } = this.context;
        const newLocation = lodash.merge({}, location, { query: { q : query, offset: 0 } });
        router.replace(newLocation);
    }

    clearSearch() {
        this.refs.FilesSearchBar.clearQuery();
        this.props.setInitialOffset(0);
        this.performSearch('');
    }

    createChangeHandler(queryKey, value){
        return (function onChangeHandler(e){
            const { location } = this.context;
            value = value || e.target.value;
            const newLocation = lodash.merge({}, location, { query: { [queryKey]: value, offset: 0 } });
            this.updateLocation(newLocation);

        }).bind(this);
    }

    getEmptyDocumentListHelper() {
        return (
            <div className={styles.helperText} data-cy="Files">
                <ListHelperText
                    primaryText={'This folder is empty, or you do not have permission to view the contents.'}  />
            </div>
        );
    }

    getFilesList(query) {
        const { documents, expirationDateDescriptorIds, documentCount, binder, isUnfiledDocuments, isLoggedInUserInternalUserForCurrentBinder, initialOffset, tasksByDocument, studyId } = this.props;

        if(this.isLoadingDocumentsBeforeAnyOffset) {
            return <LoadingStatus itemName='Documents' loading={true} filterQuery={false}/>;
        }

        const fileFactory = dataItem => {
            const tasks = lodash.get(tasksByDocument, String(dataItem.id), {});

            const expiration = isLoggedInUserInternalUserForCurrentBinder ? getExpirationDateFromDocument(dataItem, expirationDateDescriptorIds) : null;
            return <File key={dataItem.id} document={dataItem} isUnfiledDocuments={isUnfiledDocuments} binderId={binder.id} tasks={tasks} expiration={expiration} studyId={studyId}/>;
        };

        const folderIsEmpty = documentCount === 0 && lodash.isEmpty(query);
        if(folderIsEmpty) {
            return this.getEmptyDocumentListHelper();
        }

        const hasMoreDocuments = Boolean((documents.length + initialOffset) < documentCount);

        return (
            <InfiniteScroll
                key={0}
                pageStart={0}
                loadMore={this.debouncedScrollToBottom}
                hasMore={hasMoreDocuments}
                useWindow={false}
                getScrollParent={this.getScrollParent}
                loader={<LoadingStatus key={1} itemName='Documents' loading={true} filterQuery={false}/>}
            >
                <ul className={styles.fileList} data-cy="Files-fileList">{documents.map(fileFactory)}</ul>
            </InfiniteScroll>
        );
    }

    getListControls() {
        const { sortOptions, location: { query } } = this.props;
        const {
            sortBy=[],
            sortOrder=[],
        } = sortOptions;

        const sort = lodash.get(query, ['s'], '');
        const order = !lodash.isEmpty(query.o) ? query.o : lodash.get(location, 'query.o', '-1');

        const sortHeader = lodash.find(sortBy, { value: sort }) || sortBy[0];
        const sortOrderHeader = lodash.find(sortOrder, {
            value: lodash.isString(order) ? parseInt(order) : order
        });

        const finalSortHeader = sortHeader ? sortHeader.key : '';
        const finalSortOrderHeader =  sortOrderHeader ? sortOrderHeader.key : '';


        const sortByOptions = sortBy.map((so, i) => {
            return {
                label: so.key,
                handler: this.createChangeHandler.call(this, 's', so.value)
            };
        });

        const sortOrderOptions = sortOrder.map((so, i) => {
            return {
                label: so.key,
                handler: this.createChangeHandler.call(this, 'o', so.value)
            };
        });

        return (
            <ListControls>
                <SearchBar ref='FilesSearchBar' placeholder='Search Files' searchFunction={this.performSearch.bind(this)} />
                <SortingOptions>
                    <SortingOption label="Sort" activeText={finalSortHeader} options={sortByOptions}/>
                    <SortingOption label="by" activeText={finalSortOrderHeader} options={sortOrderOptions}/>
                </SortingOptions>
            </ListControls>
        );
    }

    get isLoadingDocumentsBeforeAnyOffset() {
        const { loadingDocuments, offset } = this.props;
        return loadingDocuments && offset === 0;
    }

    render() {
        const {
            isUnfiledDocuments,
            loadingDocumentsError,
            allowedMimeTypes,
            fileExtensionLookup,
            initialOffset,
            documentCount,
            setInitialOffset,
            institutionId,
            binder,
            folderId,
            location: { query },
        } = this.props;
        const { location, store } = this.context;

        if (loadingDocumentsError) {
            return (
                <div className={styles.errorPage} data-cy="Files-errorPage">
                    An error has occurred when loading the files for this binder.
                </div>
            );
        }

        if(isUnfiledDocuments) {
            return this.getUnfiledDocuments();
        }

        const q = lodash.get(query, 'q', '');

        return (
            <div className={styles.page} data-cy="Files-getListControls">
                {this.getListControls()}
                <SearchFeedback query={q} count={documentCount} clearFunction={this.clearSearch.bind(this, '')} loading={this.isLoadingDocumentsBeforeAnyOffset}/>
                <ShowPreviousRecordsButton initialOffset={initialOffset}
                    documentCount={documentCount}
                    changeQueryKey={this.changeQueryKey.bind(this)}
                    setInitialOffset={setInitialOffset}
                    loadDocuments={this.loadDocuments.bind(this)}
                    binder={binder}
                    folderId={folderId}
                    query={query}/>
                {this.getFilesList(q)}
                <FilePicker
                    ref="filePicker"
                    location={location}
                    onClose={() => this.changeQueryKey('upload', null)}
                    binderId={lodash.get(binder, 'id')}
                    institutionId = {institutionId}
                    allowedMimeTypes={allowedMimeTypes}
                    fileExtensionLookup={fileExtensionLookup}
                    store={store} />
            </div>
        );
    }

    _buildSidebarActions(binder, nextLocation) {
        const { setSidebarLinks, setSidebarIcons, location, canSeeUnfiledDocuments } = this.props;
        const links = buildSidebarActions(binder, nextLocation || location, canSeeUnfiledDocuments);
        setSidebarIcons(iconStyles.folderIconSlate);
        setSidebarLinks(links);
    }
}

const ShowPreviousRecordsButton = ({
    initialOffset,
    documentCount,
    changeQueryKey,
    setInitialOffset,
    loadDocuments,
    binder,
    folderId,
    query
}) => {
    if (documentCount <= 0 || initialOffset <= 0) {
        return null;
    }

    const onClick = e => {
        changeQueryKey('offset', 0);
        setInitialOffset(0);
        query = lodash.cloneDeep(query);
        query.offset = 0;
        loadDocuments(binder, folderId, query, 'replace');
    };

    return (
        <div className={styles.showPreviousRecordsSection} data-cy="Files-ShowPreviousRecordsButton">
            <button onClick={onClick}
                className={styles.showPreviousRecordsButton}
                data-cy="Files-button">There are records that appear before this. Clear here
                to see them.
            </button>
        </div>
    );
};

Files.propTypes = {
    setSidebarLinks: PropTypes.func.isRequired,
    setSidebarIcons: PropTypes.func.isRequired,
    setSidebarCollapsed: PropTypes.func.isRequired,
    reloadBinder: PropTypes.func.isRequired,
};

Files.contextTypes = {
    router: PropTypes.object.isRequired,
    location: locationShape.isRequired,
    store: PropTypes.any
};

export default connect((state, props) => {
    const binder = lodash.get(state, 'binder', props.binder);
    const studyId = lodash.get(state, 'study.id', null);
    const institutionId = binderInstitutionIdSelector(state);
    const documents = lodash.get(state, 'files.documents', []);
    const expirationDateDescriptorIds = lodash.get(state, 'descriptorsBySystemName.expiration_date', []);
    const tasksByDocument = lodash.get(state, 'binder.tasksByDocument', {});
    const initialOffset = lodash.get(state, 'files.initialOffset', null);
    const loadingDocuments = lodash.get(state, 'files.loadingDocuments', null);
    const loadingDocumentsError = lodash.get(state, 'files.loadingDocumentsError', false);
    const unfiledDocumentsLoadingError = lodash.get(state, 'files.unfiledDocumentsLoadingError', false);
    const documentCount = lodash.get(state, 'files.documentCount', null);
    const hasNewDocumentConversionResult = lodash.get(state, 'files.hasNewDocumentConversionResult');
    const sortOptions = lodash.get(state, 'app.fileSortingOptions.sortOptions', {});
    let { locationBeforeTransitions: { query: { limit, offset, folder } } } = state.routing;
    const binderPermissions = lodash.get(state, 'me.binderPermissions', {});
    const canSeeUnfiledDocuments = unfiledDocumentsPredicate(binderPermissions);
    const isUnfiledDocuments = `${folder}`.toLowerCase() === 'unfiled' && canSeeUnfiledDocuments;
    const allowedMimeTypes = lodash.get(state, 'app.allowedMimeTypes');
    const fileExtensionLookup = lodash.get(state, 'app.fileExtensionLookup');
    limit = parseInt(limit) || DEFAULT_LIMIT;
    offset = parseInt(offset) || DEFAULT_OFFSET;
    const canManageTasks = manageTasksSelector(state);
    const isLoggedInUserInternalUserForCurrentBinder = isLoggedInUserInternalUserForCurrentBinderSelector(state);
    return {
        binder,
        studyId,
        tasksByDocument,
        institutionId,
        documents,
        expirationDateDescriptorIds,
        initialOffset,
        allowedMimeTypes,
        loadingDocuments,
        fileExtensionLookup,
        loadingDocumentsError,
        hasNewDocumentConversionResult,
        unfiledDocumentsLoadingError,
        documentCount,
        folderId: folder,
        sortOptions,
        limit,
        offset,
        isUnfiledDocuments,
        canManageTasks,
        isLoggedInUserInternalUserForCurrentBinder,
        canSeeUnfiledDocuments,
    };
}, {
    loadDocuments: lodash.throttle(loadDocuments, 10),
    loadTasksByBinderId: lodash.throttle(loadTasksByBinderId, 10),
    loadDescriptorSystemNames,
    setSortingOptions,
    setActionButton,
    loadMimeTypes,
    clearActionButton,
    setInitialOffset: setFileState.bind(null, 'initialOffset'),
    removeFiles,
})(Files);
